import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { fontColorArr, fontsizeArr } from "../../utils/general";

function TextInputWidgetOption(props) {
  const [inputName, setInputName] = useState("Text Input");
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [status, setStatus] = useState("required");
  const [defaultValue, setDefaultValue] = useState("");
  const statusArr = ["required", "optional"];
  const [maxLength, setMaxLength] = useState("");
  const [minLength, setMinLength] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const resetState = () => {
    setInputName("Text Input");
    setIsReadOnly(false);
    setStatus("required");
    setDefaultValue("");
    setMaxLength("");
    setMinLength("");
    setValidationErrors({});
  };

  useEffect(() => {
    if (props.currWidgetsDetails?.options?.name) {
      setInputName(props.currWidgetsDetails?.options?.name);
      setIsReadOnly(props.currWidgetsDetails?.options?.isReadOnly);
      setStatus(props.currWidgetsDetails?.options?.status || "required");
      setDefaultValue(props.currWidgetsDetails?.options?.defaultValue || "");
      setMaxLength(props.currWidgetsDetails?.options?.maxLength || "");
      setMinLength(props.currWidgetsDetails?.options?.minLength || "");
    } else {
      setStatus("required");
      resetState();
    }
  }, [props.currWidgetsDetails]);

  const validateInputs = () => {
    const errors = {};

    // Validate Min Length
    if (minLength !== "" && maxLength !== "") {
      const min = parseInt(minLength);
      const max = parseInt(maxLength);

      if (isNaN(min) || isNaN(max)) {
        errors.minMax = "Min and Max length must be valid numbers";
      } else if (min > max) {
        errors.minMax = "Min length cannot be greater than Max length";
      } else if (min < 0 || max < 0) {
        errors.minMax = "Min and Max length must be positive numbers";
      }
    }

    // Validate individual Min Length
    if (
      minLength !== "" &&
      (isNaN(parseInt(minLength)) || parseInt(minLength) < 0)
    ) {
      errors.minLength = "Min length must be a valid positive number";
    }

    // Validate individual Max Length
    if (
      maxLength !== "" &&
      (isNaN(parseInt(maxLength)) || parseInt(maxLength) < 0)
    ) {
      errors.maxLength = "Max length must be a valid positive number";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveOption = () => {
    if (!validateInputs()) {
      return; // Don't save if validation fails
    }

    props.handleSaveWidgetsOptions(
      inputName,
      null, // dropdownOptions - not needed for text input
      null, // minCount - not needed for text input
      null, // maxCount - not needed for text input
      isReadOnly,
      null, // addOption - not needed for text input
      null, // deleteOption - not needed for text input
      status,
      defaultValue,
      null, // isHideLabel - not needed for text input
      maxLength,
      minLength,
    );
    resetState();
  };

  const modalContent = (
    <div
      className={`fixed inset-0 overflow-y-auto ${
        props.showDropdown ? "block" : "hidden"
      }`}
      style={{ zIndex: 9999 }}
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveOption();
            }}
          >
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {props.title}
                  </h3>

                  <div className="mt-4 space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        required
                        defaultValue={inputName}
                        value={inputName}
                        onChange={(e) => setInputName(e.target.value)}
                        className="appearance-none rounded relative block w-full px-3 py-2 border-2 border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="appearance-none rounded relative block w-full px-3 py-2 border-2 border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                      >
                        {statusArr.map((statusOption, ind) => (
                          <option key={ind} value={statusOption}>
                            {statusOption.charAt(0).toUpperCase() +
                              statusOption.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Default Value
                      </label>
                      <input
                        type="text"
                        value={defaultValue}
                        onChange={(e) => setDefaultValue(e.target.value)}
                        className="appearance-none rounded relative block w-full px-3 py-2 border-2 border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                        placeholder="Enter default value (optional)"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Font Size
                        </label>
                        <select
                          className="rounded relative block w-full px-3 py-2 border-2 border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                          value={
                            props.fontSize ||
                            props.currWidgetsDetails?.options?.fontSize ||
                            12
                          }
                          onChange={(e) =>
                            props.setFontSize(parseInt(e.target.value))
                          }
                        >
                          {fontsizeArr.map((size, ind) => (
                            <option key={ind} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Color
                        </label>
                        <select
                          value={
                            props.fontColor ||
                            props.currWidgetsDetails?.options?.fontColor ||
                            "black"
                          }
                          onChange={(e) => props.setFontColor(e.target.value)}
                          className="rounded relative block w-full px-3 py-2 border-2 border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                        >
                          {fontColorArr.map((color, ind) => (
                            <option key={ind} value={color}>
                              {color}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Min Length
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={minLength}
                          onChange={(e) => setMinLength(e.target.value)}
                          className={`appearance-none rounded relative block w-full px-3 py-2 border-2 ${
                            validationErrors.minLength ||
                            validationErrors.minMax
                              ? "border-red-500"
                              : "border-gray-300"
                          } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm`}
                          placeholder="Enter min length (optional)"
                        />
                        {validationErrors.minLength && (
                          <p className="mt-1 text-sm text-red-600">
                            {validationErrors.minLength}
                          </p>
                        )}
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Max Length
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={maxLength}
                          onChange={(e) => setMaxLength(e.target.value)}
                          className={`appearance-none rounded relative block w-full px-3 py-2 border-2 ${
                            validationErrors.maxLength ||
                            validationErrors.minMax
                              ? "border-red-500"
                              : "border-gray-300"
                          } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm`}
                          placeholder="Enter max length (optional)"
                        />
                        {validationErrors.maxLength && (
                          <p className="mt-1 text-sm text-red-600">
                            {validationErrors.maxLength}
                          </p>
                        )}
                      </div>
                    </div>

                    {validationErrors.minMax && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600">
                          {validationErrors.minMax}
                        </p>
                      </div>
                    )}

                    {/* <div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <input
                            id="isreadonly"
                            type="checkbox"
                            checked={isReadOnly}
                            onChange={(e) => setIsReadOnly(e.target.checked)}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <label
                            htmlFor="isreadonly"
                            className="ml-2 text-sm text-gray-700"
                          >
                            Read Only
                          </label>
                        </div>
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
              >
                Save
              </button>
              {props.currWidgetsDetails?.options?.name && (
                <button
                  type="button"
                  onClick={() => {
                    props.handleClose && props.handleClose();
                    resetState();
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return props.showDropdown ? createPortal(modalContent, document.body) : null;
}

export default TextInputWidgetOption;
